import * as React from "react";

import { useSelector } from "react-redux";

import { useIntl } from "react-intl-compat";
import {
  useGetListingAmenitiesQuery,
  AmenityOption,
} from "~/services/env.service";

import {
  selectIsInstantBookEligibleHost,
  selectProfileSSOid,
} from "../../../../components/src/app-main/selectors/profile.selectors";
import type { ListingUseType } from "../../../../components/src/services/listings.service";
import { useGetNonDeactivatedListingsQuery } from "../../../../components/src/services/listings.service";
import { useGetSpaceQuery } from "../../../../components/src/services/spaces.service";

import {
  AMENITIES_GROUP_BY_USE_TYPE,
  LISTING_USE_TYPE_CONFIGS,
} from "./ActivateListingsPage.consts";

type UseActivateListingsDataArgs = {
  spaceId: string;
};

export const useActivateListingsData = ({
  spaceId,
}: UseActivateListingsDataArgs) => {
  const intl = useIntl();
  const ssoId = useSelector(selectProfileSSOid);
  const isInstantBookEligible = useSelector(selectIsInstantBookEligibleHost);

  const { data: space, isFetching: isSpaceFetching } = useGetSpaceQuery(
    { spaceId },
    { skip: !spaceId },
  );

  const { data: ownerListings, isFetching: isListingsFetching } =
    useGetNonDeactivatedListingsQuery({ ssoId: ssoId ?? "" }, { skip: !ssoId });

  const { data: amenitiesResponse, isFetching: isAmenitiesFetching } =
    useGetListingAmenitiesQuery({ locale: intl.locale });

  const inactiveUseTypes = React.useMemo(() => {
    // Determine which listing use types are already activated for this space
    const activatedUseTypes = new Set<ListingUseType>(
      (ownerListings?.listings ?? [])
        .filter((listing) => listing.parentSpaceId === spaceId)
        .map((listing) => listing.use_type as ListingUseType)
        .filter(Boolean),
    );

    return LISTING_USE_TYPE_CONFIGS.filter(
      (config) => !activatedUseTypes.has(config.useType),
    );
  }, [ownerListings, spaceId]);

  const amenitiesByUseType = React.useMemo(() => {
    const amenities = amenitiesResponse?.amenities;

    return Object.fromEntries(
      Object.entries(AMENITIES_GROUP_BY_USE_TYPE).map(([useType, group]) => [
        useType,
        amenities?.[group] ?? [],
      ]),
    ) as Record<ListingUseType, AmenityOption[]>;
  }, [amenitiesResponse]);

  return {
    space,
    inactiveUseTypes,
    amenitiesByUseType,
    isInstantBookEligible,
    isFetching: isSpaceFetching || isListingsFetching || isAmenitiesFetching,
  };
};
